// Namespace prefix registry — the merged view of compiled-in defaults plus
// the bindings persisted for this site. Read-only for User/Auditor; PowerUser
// and above add, edit and delete. Lives on Database Management: prefixes are
// how the store spells its data, not a personal preference (moved back
// 2026-09-09).

import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteNamespace,
  listNamespaces,
  namespaceUsageStatus,
  removeUnusedNamespaces,
  runNamespaceUsageCheck,
  upsertNamespace,
  type NamespaceList,
  type NamespaceUsageStatus,
  type RemoveUnusedResult,
} from '@/lib/api';
import { canImportData, canManageDashboards, canManageSettings, useMe } from '@/lib/useMe';
import { foldNamespaces, mergeNamespacePrefix, type NamespaceFoldReport } from '@/lib/useAdmin';
import {
  invalidAdvice,
  prefixCount,
  removableRows,
  usageCheckMessage,
  usageLabel,
  visibleRows,
} from '@/lib/namespaceUsage';
import { cn } from '@/lib/utils';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

/** How often the panel asks again while the prefix usage check runs or waits. */
const USAGE_POLL_MS = 5_000;

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function NamespaceRegistryPanel() {
  const auth = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const token = auth.user?.id_token;
  const allowed = canImportData(me.data); // PowerUser+ — server enforces.
  const canCheckUsage = canManageDashboards(me.data); // The namespace edit permission.
  const manageSettings = canManageSettings(me.data); // Admin/Owner — the re-fold.
  const [foldReport, setFoldReport] = useState<{ dryRun: boolean; report: NamespaceFoldReport } | null>(null);
  const [foldError, setFoldError] = useState<string | null>(null);
  const [foldBusy, setFoldBusy] = useState<'count' | 'fold' | null>(null);
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [removeResult, setRemoveResult] = useState<RemoveUnusedResult | null>(null);
  const [removeUnusedError, setRemoveUnusedError] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  // The prefix being merged away, the spelling chosen to keep, and what the
  // count said — one row opens at a time.
  const [mergeFrom, setMergeFrom] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState('');
  const [mergeBusy, setMergeBusy] = useState<'count' | 'merge' | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeReport, setMergeReport] = useState<{ dryRun: boolean; report: NamespaceFoldReport } | null>(null);

  function openMerge(prefix: string) {
    setMergeFrom(prefix);
    setMergeInto('');
    setMergeError(null);
    setMergeReport(null);
  }

  async function runMerge(dryRun: boolean) {
    if (!token) return;
    if (!mergeFrom) return;
    if (!mergeInto) {
      setMergeError('Choose the prefix to keep.');
      return;
    }
    if (
      !dryRun &&
      !window.confirm(
        `Rewrite every term spelled ${mergeFrom}: as ${mergeInto}:? The data is rewritten in place, the index is rebuilt afterwards, and ${mergeFrom} leaves this list. Count first if you have not.`,
      )
    ) {
      return;
    }
    setMergeBusy(dryRun ? 'count' : 'merge');
    setMergeError(null);
    const r = await mergeNamespacePrefix(
      { from: mergeFrom, into: mergeInto, dry_run: dryRun },
      token,
    );
    setMergeBusy(null);
    if (r.ok) {
      setMergeReport({ dryRun, report: r.data });
      if (!dryRun) {
        setMergeFrom(null);
        void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
      }
    } else {
      setMergeError(r.message);
    }
  }

  async function runFold(dryRun: boolean) {
    if (!token) return;
    if (
      !dryRun &&
      !window.confirm(
        'Rewrite older data to the short name of every prefix in the list? The data is rewritten where it is and the index is rebuilt afterwards. On a large site this can run for an hour. Count first if you have not.',
      )
    ) {
      return;
    }
    setFoldBusy(dryRun ? 'count' : 'fold');
    setFoldError(null);
    const r = await foldNamespaces({ dry_run: dryRun }, token);
    setFoldBusy(null);
    if (r.ok) {
      setFoldReport({ dryRun, report: r.data });
      if (!dryRun) void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    } else {
      setFoldError(r.message);
    }
  }

  const list = useQuery<NamespaceList, Error>({
    queryKey: ['namespaces'],
    enabled: !!token,
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await listNamespaces(token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });

  const usage = useQuery<NamespaceUsageStatus, Error>({
    queryKey: ['namespace-usage'],
    enabled: !!token,
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await namespaceUsageStatus(token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    refetchInterval: (query) => (query.state.data?.running ? USAGE_POLL_MS : false),
  });

  // A finished check changes every row's usage.
  const finishedAt = usage.data?.last?.finished_at;
  useEffect(() => {
    if (finishedAt) void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
  }, [finishedAt, queryClient]);

  const [newPrefix, setNewPrefix] = useState('');
  const [newUri, setNewUri] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  // Deletion reports next to the list, not under the add form — its button is
  // up there and the two failures are independent.
  const [removeError, setRemoveError] = useState<string | null>(null);

  const upsert = useMutation({
    mutationFn: async ({ prefix, uri }: { prefix: string; uri: string }) => {
      if (!token) throw new Error('no token');
      const r = await upsertNamespace(prefix, uri, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    onSuccess: () => {
      setNewPrefix('');
      setNewUri('');
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const remove = useMutation({
    mutationFn: async (prefix: string) => {
      if (!token) throw new Error('no token');
      const r = await deleteNamespace(prefix, token);
      if (!r.ok) throw new Error(r.message);
    },
    onSuccess: () => {
      setRemoveError(null);
      void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    },
    onError: (e: Error) => setRemoveError(e.message),
  });

  const checkUsage = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('no token');
      const r = await runNamespaceUsageCheck(token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    onSuccess: (status) => {
      setCheckError(null);
      setReviewing(false);
      setRemoveResult(null);
      queryClient.setQueryData(['namespace-usage'], status);
    },
    onError: (e: Error) => setCheckError(e.message),
  });

  const removeUnused = useMutation({
    mutationFn: async (names: string[]) => {
      if (!token) throw new Error('no token');
      const r = await removeUnusedNamespaces(names, token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
    onSuccess: (result) => {
      setReviewing(false);
      setRemoveResult(result);
      setRemoveUnusedError(null);
      void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    },
    onError: (e: Error) => {
      setReviewing(false);
      setRemoveUnusedError(e.message);
      void queryClient.invalidateQueries({ queryKey: ['namespaces'] });
    },
  });

  const registry = list.data;
  const prefixes = registry?.prefixes ?? [];
  const candidates = removableRows(prefixes);
  const shown = visibleRows(prefixes, unusedOnly);
  const checking = !!usage.data?.running;
  const checkMessage = usage.data ? usageCheckMessage(usage.data, when) : null;

  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-tres-text-primary">Namespace prefixes</h2>
      <p className="mt-1 text-xs text-tres-text-secondary">
        Prefixes available in queries and used by the UI to display URIs as compact names
        (e.g. <code className="text-xs">ex:Product</code>). Defaults are compiled in; additions
        and overrides persist per instance and apply to every user.
      </p>

      {list.isLoading && (
        <p className="mt-3 text-xs text-tres-text-secondary">Loading…</p>
      )}
      {list.isError && (
        <p className="mt-3 text-xs text-destructive">
          Could not load prefixes: {list.error.message}
        </p>
      )}

      {removeError && (
        <p className="mt-3 text-xs text-destructive" role="alert">
          Could not delete prefix: {removeError}
        </p>
      )}

      {registry && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          {canCheckUsage && (
            <button
              type="button"
              onClick={() => checkUsage.mutate()}
              disabled={checking || checkUsage.isPending}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Check which prefixes are used
            </button>
          )}
          {canCheckUsage && (
            <button
              type="button"
              onClick={() => {
                setRemoveResult(null);
                setRemoveUnusedError(null);
                setReviewing(true);
              }}
              disabled={!registry.usage_current || candidates.length === 0 || removeUnused.isPending}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Remove unused{registry.usage_current ? ` (${candidates.length.toLocaleString()})` : ''}
            </button>
          )}
          <label
            className={cn(
              'flex items-center gap-2 text-tres-text-primary',
              registry.usage_checked_at === null && 'opacity-50',
            )}
          >
            <input
              type="checkbox"
              checked={unusedOnly}
              disabled={registry.usage_checked_at === null}
              onChange={(e) => setUnusedOnly(e.target.checked)}
            />
            Show only unused
          </label>
        </div>
      )}
      {registry && (
        <div className="mt-2 space-y-1 text-xs text-tres-text-secondary" role="status">
          {checkMessage && <p>{checkMessage}</p>}
          {!checking && registry.usage_checked_at === null && (
            <p>
              Which prefixes your data uses is not checked yet. The check reads every statement once
              and runs in the background; on a large store it takes minutes.
            </p>
          )}
          {!checking && registry.usage_checked_at !== null && !registry.usage_current && (
            <p>
              The data may have changed since the check at {when(registry.usage_checked_at)}. Check
              again before removing unused prefixes.
            </p>
          )}
        </div>
      )}
      {checkError && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          Could not start the check: {checkError}
        </p>
      )}

      {reviewing && (
        <div className={cn(messageClass('platform', 'compact', 'warning'), 'mt-3')}>
          <p className="text-tres-text-primary">
            No statement in your data uses these {prefixCount(candidates.length)}. Removing them takes
            them out of this list and out of query resolution; a file that uses one later records it
            again when it loads. Built-in prefixes and ones added by hand are never removed here.
          </p>
          <ul className="mt-2 max-h-48 overflow-y-auto font-mono text-tres-text-secondary">
            {candidates.map((p) => (
              <li key={p.prefix} className="break-all">
                {p.prefix} — {p.uri}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => removeUnused.mutate(candidates.map((p) => p.prefix))}
              disabled={removeUnused.isPending || candidates.length === 0}
              className={cn(
                'rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground',
                'hover:bg-primary/90 disabled:opacity-50',
              )}
            >
              {removeUnused.isPending ? 'Removing…' : `Remove ${prefixCount(candidates.length)}`}
            </button>
            <button
              type="button"
              onClick={() => setReviewing(false)}
              disabled={removeUnused.isPending}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {removeUnusedError && (
        <p className="mt-3 text-xs text-destructive" role="alert">
          Could not remove unused prefixes: {removeUnusedError}
        </p>
      )}
      {removeResult && (
        <div className="mt-3 text-xs text-tres-text-secondary" role="status">
          <p>Removed {prefixCount(removeResult.removed.length)}.</p>
          {removeResult.kept.length > 0 && (
            <ul className="mt-1">
              {removeResult.kept.map((k) => (
                <li key={k.prefix}>
                  Kept <span className="font-mono">{k.prefix}</span>: {k.reason}.
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mergeReport && !mergeReport.dryRun && (
        <p className="mt-3 text-xs text-tres-text-secondary" role="status">
          Merged <span className="tabular-nums">{mergeReport.report.rewritten.toLocaleString()}</span>{' '}
          statements onto their kept spelling;{' '}
          <span className="tabular-nums">{mergeReport.report.merged.toLocaleString()}</span> met a
          term the store already held. The index is rebuilding.
        </p>
      )}

      {registry && (
        <ul className="mt-4 divide-y divide-border rounded-md border border-border">
          {shown.length === 0 && (
            <li className="px-3 py-2 text-xs text-tres-text-secondary">
              {unusedOnly ? 'Every checked prefix is used by your data.' : 'No prefixes.'}
            </li>
          )}
          {shown.map((p) => {
            const label = usageLabel(p, registry, when);
            const advice = invalidAdvice(p, registry);
            return (
              <li key={p.prefix} className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs">
                <span className="flex w-28 shrink-0 gap-1">
                  {allowed && !p.builtin && !p.imported && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(p.prefix)}
                      disabled={remove.isPending}
                      className={cn(
                        'rounded border border-border bg-background px-2 py-0.5 text-[11px] text-tres-text-primary',
                        'hover:bg-muted disabled:opacity-50',
                      )}
                      title="Delete this prefix"
                    >
                      Delete
                    </button>
                  )}
                  {manageSettings && !p.builtin && (
                    <button
                      type="button"
                      onClick={() => openMerge(p.prefix)}
                      disabled={mergeBusy !== null}
                      className={cn(
                        'rounded border border-border bg-background px-2 py-0.5 text-[11px] text-tres-text-primary',
                        'hover:bg-muted disabled:opacity-50',
                      )}
                      title={`Rewrite every term spelled ${p.prefix}: to use another prefix`}
                    >
                      Merge
                    </button>
                  )}
                </span>
                <span className="w-24 break-all font-mono font-medium text-tres-text-primary">
                  {p.prefix}
                </span>
                <span className="flex-1 break-all font-mono text-tres-text-secondary">{p.uri}</span>
                <span
                  className={cn(
                    'rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                    p.builtin
                      ? 'bg-muted text-tres-text-secondary'
                      : 'bg-tres-action/15 text-tres-action',
                  )}
                >
                  {p.builtin ? 'Built-in' : p.imported ? 'From your data' : 'Custom'}
                </span>
                {p.invalid !== null && (
                  <span className="rounded bg-destructive/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-destructive">
                    Not a valid prefix
                  </span>
                )}
                {label && (
                  <span
                    className={cn(
                      'basis-full pl-[4.25rem] tabular-nums sm:basis-auto sm:pl-0',
                      p.usage === 0 && registry.usage_current
                        ? 'text-tres-warning'
                        : 'text-tres-text-secondary',
                    )}
                  >
                    {label}
                  </span>
                )}
                {advice && (
                  <p className="basis-full pl-[4.25rem] text-tres-text-secondary">{advice}</p>
                )}
                {mergeFrom === p.prefix && (
                  <div className="basis-full rounded-md border border-border bg-muted/40 p-3">
                    <p className="text-tres-text-secondary">
                      Every term spelled <span className="font-mono">{p.prefix}:</span> is rewritten
                      to the prefix you keep. A term that already exists under that spelling merges
                      into it, and <span className="font-mono">{p.prefix}</span> leaves this list.
                      The index is rebuilt afterwards.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <label className="text-tres-text-secondary" htmlFor={`merge-into-${p.prefix}`}>
                        Keep
                      </label>
                      <select
                        id={`merge-into-${p.prefix}`}
                        value={mergeInto}
                        onChange={(e) => setMergeInto(e.target.value)}
                        className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-tres-text-primary"
                      >
                        <option value="">Choose a prefix…</option>
                        {prefixes
                          .filter((other) => other.prefix !== p.prefix)
                          .map((other) => (
                            <option key={other.prefix} value={other.prefix}>
                              {other.prefix} — {other.uri}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => void runMerge(true)}
                        disabled={mergeBusy !== null}
                        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                      >
                        {mergeBusy === 'count' ? 'Counting…' : 'Count what would move'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void runMerge(false)}
                        disabled={mergeBusy !== null}
                        className={buttonVariants({ variant: 'primary', size: 'sm' })}
                      >
                        {mergeBusy === 'merge' ? 'Merging…' : 'Merge'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setMergeFrom(null)}
                        disabled={mergeBusy !== null}
                        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                      >
                        Cancel
                      </button>
                    </div>
                    {mergeError && (
                      <p className="mt-2 text-destructive" role="alert">
                        {mergeError}
                      </p>
                    )}
                    {mergeReport?.dryRun && (
                      <p className="mt-2 text-tres-text-secondary" role="status">
                        Would move{' '}
                        <span className="tabular-nums">
                          {mergeReport.report.rewritten.toLocaleString()}
                        </span>{' '}
                        of <span className="tabular-nums">
                          {mergeReport.report.scanned.toLocaleString()}
                        </span>{' '}
                        statements.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {allowed && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            if (!newPrefix.trim() || !newUri.trim()) {
              setFormError('Prefix and URI are both required.');
              return;
            }
            upsert.mutate({ prefix: newPrefix.trim(), uri: newUri.trim() });
          }}
        >
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wide text-tres-text-secondary">
              Prefix
            </label>
            <input
              type="text"
              value={newPrefix}
              onChange={(e) => setNewPrefix(e.target.value)}
              placeholder="ex"
              className="mt-1 w-32 rounded border border-border bg-background px-2 py-1 font-mono text-sm"
              disabled={upsert.isPending}
            />
          </div>
          <div className="flex flex-1 flex-col">
            <label className="text-[10px] uppercase tracking-wide text-tres-text-secondary">
              URI
            </label>
            <input
              type="text"
              value={newUri}
              onChange={(e) => setNewUri(e.target.value)}
              placeholder="http://example.org/ns#"
              className="mt-1 w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
              disabled={upsert.isPending}
            />
          </div>
          <button
            type="submit"
            disabled={upsert.isPending}
            className={cn(
              'rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground',
              'hover:bg-primary/90 disabled:opacity-50',
            )}
          >
            {upsert.isPending ? 'Saving…' : 'Add / update'}
          </button>
          {formError && (
            <p className="basis-full text-xs text-destructive">{formError}</p>
          )}
        </form>
      )}
      {!allowed && (
        <p className="mt-3 text-xs text-tres-text-secondary">
          Adding or removing prefixes requires PowerUser or Admin role.
        </p>
      )}
      {manageSettings && (
        <div className="mt-6 border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-tres-text-primary">
            Make older data use its prefix
          </h3>
          <p className="mt-1 text-xs text-tres-text-secondary">
            Data loaded before its prefix was added here is stored under the full web address,
            such as <span className="font-mono">http://example.org/film#Alien</span>. Queries and
            newer data use the short name, <span className="font-mono">film:Alien</span>, so a
            query written with the short name does not find the older data. This rewrites the
            older data to the short name, for every prefix in the list above. Adding a prefix
            already does it for that one prefix. Count first: counting shows how many statements
            would change, and changes nothing.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void runFold(true)}
              disabled={foldBusy !== null}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              {foldBusy === 'count' ? 'Counting…' : 'Count what would change'}
            </button>
            <button
              type="button"
              onClick={() => void runFold(false)}
              disabled={foldBusy !== null}
              className={cn(
                'rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground',
                'hover:bg-primary/90 disabled:opacity-50',
              )}
            >
              {foldBusy === 'fold' ? 'Rewriting…' : 'Rewrite older data'}
            </button>
          </div>
          {foldError && <p className="mt-2 text-xs text-destructive">{foldError}</p>}
          {foldReport && (
            <div className="mt-3 text-xs text-tres-text-secondary">
              <p>
                {foldReport.dryRun ? 'Would change' : 'Changed'}{' '}
                <span className="tabular-nums">{foldReport.report.rewritten.toLocaleString()}</span> of{' '}
                <span className="tabular-nums">{foldReport.report.scanned.toLocaleString()}</span> statements
                {!foldReport.dryRun && foldReport.report.merged > 0 && (
                  <>
                    ; {foldReport.report.merged.toLocaleString()} joined a statement already
                    stored under the short name
                  </>
                )}
                {!foldReport.dryRun && foldReport.report.rewritten > 0 && <>; the index is rebuilding</>}
                .
              </p>
              {foldReport.report.by_prefix.length > 0 && (
                <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  {foldReport.report.by_prefix.map(([prefix, n]) => (
                    <li key={prefix} className="font-mono">
                      {prefix || '(no prefix)'}:{' '}
                      <span className="tabular-nums">{n.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
